import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../dashboard/DashboardIcons';

const AR_MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
const AR_DAYS = ['أحد', 'إثن', 'ثلا', 'أرب', 'خمي', 'جمع', 'سبت'];

const pad2 = (n) => String(n).padStart(2, '0');
const toISO = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;
const parseISO = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return { y, m: m - 1, d };
};
const fmtDisplay = (iso) => {
  const p = parseISO(iso);
  if (!p) return '';
  return `${p.d} ${AR_MONTHS[p.m]} ${p.y}`;
};
const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const startWeekday = (y, m) => new Date(y, m, 1).getDay();

/** تقويم عربي فخم — بديل عن input[type=date] البدائي، بلا مكتبات خارجية
 *  inline=true: يعرض شبكة التقويم مباشرة بلا زر/نافذة منبثقة (للاستخدام داخل لوحة مفتوحة مسبقاً) */
export default function PremiumDatePicker({ value, onChange, placeholder = 'اختر تاريخاً', testId, allowClear = true, disabled = false, minDate, maxDate, inline = false }) {
  const [open, setOpen] = useState(inline);
  const today = new Date();
  const selected = parseISO(value);
  const [viewY, setViewY] = useState(selected?.y || today.getFullYear());
  const [viewM, setViewM] = useState(selected?.m ?? today.getMonth());
  const ref = useRef(null);

  useEffect(() => {
    if (selected) { setViewY(selected.y); setViewM(selected.m); }
  }, [value]);

  const closeOnOutside = useCallback((e) => {
    if (ref.current && !ref.current.contains(e.target)) setOpen(false);
  }, []);

  useEffect(() => {
    if (open && !inline) document.addEventListener('mousedown', closeOnOutside);
    return () => document.removeEventListener('mousedown', closeOnOutside);
  }, [open, closeOnOutside, inline]);

  const changeMonth = (delta) => {
    let m = viewM + delta, y = viewY;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setViewM(m); setViewY(y);
  };

  const pickDay = (d) => {
    const iso = toISO(viewY, viewM, d);
    if (minDate && iso < minDate) return;
    if (maxDate && iso > maxDate) return;
    onChange(iso);
    if (!inline) setOpen(false);
  };

  const firstWeekday = startWeekday(viewY, viewM);
  const totalDays = daysInMonth(viewY, viewM);
  const cells = [...Array(firstWeekday).fill(null), ...Array.from({ length: totalDays }, (_, i) => i + 1)];
  const isToday = (d) => d === today.getDate() && viewM === today.getMonth() && viewY === today.getFullYear();
  const isSelected = (d) => selected && d === selected.d && viewM === selected.m && viewY === selected.y;
  const isDisabled = (d) => {
    const iso = toISO(viewY, viewM, d);
    return (minDate && iso < minDate) || (maxDate && iso > maxDate);
  };

  return (
    <div className={inline ? '' : 'relative'} ref={ref}>
      {!inline && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          data-testid={testId}
          className="flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 transition-colors hover:border-teal-300 focus:border-teal-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
        >
          <span className={value ? 'font-semibold text-slate-800' : 'text-slate-400'}>{value ? fmtDisplay(value) : placeholder}</span>
          <Icon name="calendar" className="h-4 w-4 shrink-0 text-teal-500" />
        </button>
      )}

      {open && (
        <div className={inline ? 'w-full' : 'absolute z-50 mt-2 w-72 rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl'} data-testid={`${testId}-panel`}>
          <div className="mb-2 flex items-center justify-between">
            <button type="button" onClick={() => changeMonth(-1)} data-testid={`${testId}-prev-month`} className="rounded-lg p-1.5 text-slate-400 hover:bg-teal-50 hover:text-teal-600">
              <Icon name="chevronDown" className="h-4 w-4 rotate-90" />
            </button>
            <div className="flex items-center gap-1.5 text-sm font-black text-slate-800">
              <span>{AR_MONTHS[viewM]}</span>
              <select value={viewY} onChange={(e) => setViewY(Number(e.target.value))} className="rounded-md bg-slate-50 px-1 py-0.5 text-sm font-bold text-slate-700 focus:outline-none">
                {(() => {
                  const maxY = maxDate ? parseISO(maxDate).y : today.getFullYear() + 5;
                  const minY = minDate ? parseISO(minDate).y : maxY - 100;
                  return Array.from({ length: maxY - minY + 1 }, (_, i) => maxY - i).map((y) => <option key={y} value={y}>{y}</option>);
                })()}
              </select>
            </div>
            <button type="button" onClick={() => changeMonth(1)} data-testid={`${testId}-next-month`} className="rounded-lg p-1.5 text-slate-400 hover:bg-teal-50 hover:text-teal-600">
              <Icon name="chevronDown" className="h-4 w-4 -rotate-90" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-slate-400">
            {AR_DAYS.map((d) => <div key={d} className="py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1 text-center">
            {cells.map((d, i) => d === null ? <div key={i} /> : (
              <button
                key={i}
                type="button"
                disabled={isDisabled(d)}
                onClick={() => pickDay(d)}
                data-testid={`${testId}-day-${toISO(viewY, viewM, d)}`}
                className={`aspect-square rounded-lg text-xs font-semibold transition-all
                  ${isSelected(d) ? 'bg-teal-600 text-white shadow-md' : isToday(d) ? 'bg-teal-50 text-teal-700 ring-1 ring-teal-300' : 'text-slate-600 hover:bg-slate-100'}
                  ${isDisabled(d) ? 'cursor-not-allowed opacity-30' : ''}`}
              >
                {d}
              </button>
            ))}
          </div>

          <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
            <button type="button" onClick={() => { const t = new Date(); setViewY(t.getFullYear()); setViewM(t.getMonth()); onChange(toISO(t.getFullYear(), t.getMonth(), t.getDate())); if (!inline) setOpen(false); }} data-testid={`${testId}-today-btn`} className="text-xs font-bold text-teal-600 hover:text-teal-700">اليوم</button>
            {allowClear && value && (
              <button type="button" onClick={() => { onChange(''); if (!inline) setOpen(false); }} data-testid={`${testId}-clear-btn`} className="text-xs font-bold text-slate-400 hover:text-red-500">مسح</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
